import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import { Button, Badge, Alert, Spinner, Select, ScopeBadges } from './shared';
import SyncPickerModal from './SyncPickerModal';

const AGENT_TYPES = ['default', 'partner', 'voice', 'specialized', 'user_created'];
const OWNER_TYPES = ['system', 'partner', 'user'];

const TYPE_VARIANT = {
    default: 'info', partner: 'purple', voice: 'success',
    specialized: 'warning', user_created: 'default',
};

export default function AdminAgents({ agentType } = {}) {
    const navigate = useNavigate();
    const isSubAgent = agentType === 'sub_agent';
    const basePath = isSubAgent ? '/admin/subagents' : '/admin/agents';
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [cacheStats, setCacheStats] = useState(null);
    const [syncLoading, setSyncLoading] = useState(false);
    const [filters, setFilters] = useState({ agent_type: agentType || '', owner_type: '' });
    const [syncPickerOpen, setSyncPickerOpen] = useState(false);

    useEffect(() => {
        loadAgents();
        loadCacheStats();
    }, [filters]);

    const loadAgents = async () => {
        try {
            setLoading(true);
            const data = await adminApi.listAgents(filters);
            let list = data?.agents || [];
            if (!agentType) {
                list = list.filter(a => a.agent_type !== 'sub_agent');
            }
            setAgents(list);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to load agents');
        } finally {
            setLoading(false);
        }
    };

    const loadCacheStats = async () => {
        try { setCacheStats(await adminApi.getAgentCacheStats()); } catch {}
    };

    const handleSyncYaml = async (force = false) => {
        try {
            setSyncLoading(true);
            setError(null);
            const result = await adminApi.syncAgentsFromYaml(force);
            const synced = result.synced?.length || 0;
            const skipped = result.skipped?.length || 0;
            let message = `Sync: ${synced} synced`;
            if (skipped > 0) message += `, ${skipped} skipped`;
            if (result.synced?.length > 0) message += ` [${result.synced.join(', ')}]`;
            if (result.errors?.length > 0) {
                setError(`Errors: ${result.errors.map(e => `${e.id}: ${e.error}`).join('; ')}`);
            }
            setSuccessMessage(message);
            loadAgents();
            loadCacheStats();
            setTimeout(() => setSuccessMessage(null), 8000);
        } catch (err) {
            setError(err.message || 'Failed to sync');
        } finally {
            setSyncLoading(false);
        }
    };

    const handleClearAllCache = async () => {
        if (!confirm('Clear all agent cache?')) return;
        try {
            await adminApi.clearAllAgentCache();
            setSuccessMessage('Cache cleared');
            loadCacheStats();
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDelete = async (e, agentId, isBuiltin) => {
        e.stopPropagation();
        const msg = isBuiltin ? 'Delete built-in agent? It will reload on restart.' : 'Delete this agent?';
        if (!confirm(msg)) return;
        try {
            await adminApi.deleteAgent(agentId, isBuiltin);
            loadAgents();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleReload = async (e, agentId) => {
        e.stopPropagation();
        try {
            await adminApi.reloadAgent(agentId);
            setSuccessMessage(`Agent ${agentId} reloaded`);
            loadAgents();
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            setError(err.message);
        }
    };

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">{isSubAgent ? 'Subagents' : 'Agents'}</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        {agents.length} {isSubAgent ? 'subagent' : 'agent'}{agents.length !== 1 ? 's' : ''}
                        {cacheStats && <span className="ml-2 text-gray-400">L1: {cacheStats.l1_cache_size} cached</span>}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {!isSubAgent && <Button variant="secondary" size="sm" onClick={handleClearAllCache}>Clear Cache</Button>}
                    {isSubAgent && <Button variant="outline" size="sm" onClick={() => setSyncPickerOpen(true)}>Sync from Git</Button>}
                    <Button size="sm" onClick={() => navigate(`${basePath}/new`)}>Create {isSubAgent ? 'Subagent' : 'Agent'}</Button>
                </div>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}
            {successMessage && <Alert variant="success" onClose={() => setSuccessMessage(null)} className="mb-4">{successMessage}</Alert>}

            {!isSubAgent && (
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <Select
                        options={[{ value: '', label: 'All Types' }, ...AGENT_TYPES.map(t => ({ value: t, label: t.replace('_', ' ') }))]}
                        value={filters.agent_type}
                        onChange={(e) => setFilters(f => ({ ...f, agent_type: e.target.value }))}
                        className="w-40"
                    />
                    <Select
                        options={[{ value: '', label: 'All Owners' }, ...OWNER_TYPES.map(t => ({ value: t, label: t }))]}
                        value={filters.owner_type}
                        onChange={(e) => setFilters(f => ({ ...f, owner_type: e.target.value }))}
                        className="w-36"
                    />
                </div>
            )}

            {loading ? (
                <Spinner size="lg" text="Loading agents..." center />
            ) : agents.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm">No agents found</div>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/60">
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Agent</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Type</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Model</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Owner</th>
                                {isSubAgent && <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Tags</th>}
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Scope</th>
                                <th className="text-center px-3 py-2.5 text-xs font-medium text-gray-500">Public</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 w-72">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {agents.map((agent) => (
                                <tr
                                    key={agent.id}
                                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                                    onClick={() => navigate(`${basePath}/${agent.id}`)}
                                >
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-gray-900 truncate max-w-[200px]">{agent.name}</span>
                                            <Badge variant="outline">{agent.id}</Badge>
                                        </div>
                                        {agent.description && (
                                            <p className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">{agent.description}</p>
                                        )}
                                    </td>
                                    <td className="px-3 py-3">
                                        <Badge variant={TYPE_VARIANT[agent.agent_type] || 'default'}>
                                            {agent.agent_type?.replace('_', ' ')}
                                        </Badge>
                                    </td>
                                    <td className="px-3 py-3">
                                        <span className="text-xs text-gray-600 font-mono">{agent.model_name}</span>
                                    </td>
                                    <td className="px-3 py-3">
                                        <Badge variant={agent.owner_type === 'system' ? 'info' : agent.owner_type === 'partner' ? 'purple' : 'success'}>
                                            {agent.owner_type}
                                        </Badge>
                                    </td>
                                    {isSubAgent && (
                                        <td className="px-3 py-3">
                                            {(agent.tags || []).length > 0 ? (
                                                <div className="flex flex-wrap gap-1">
                                                    {(agent.tags || []).slice(0, 4).map((t) => (
                                                        <Badge key={t} variant="info">#{t}</Badge>
                                                    ))}
                                                    {agent.tags.length > 4 && <Badge variant="outline">+{agent.tags.length - 4}</Badge>}
                                                </div>
                                            ) : (
                                                <span className="text-gray-300">-</span>
                                            )}
                                        </td>
                                    )}
                                    <td className="px-3 py-3">
                                        <ScopeBadges environment={agent.environment} tags={isSubAgent ? undefined : agent.tags} />
                                    </td>
                                    <td className="px-3 py-3 text-center">
                                        <span className={`inline-block w-2 h-2 rounded-full ${agent.is_public ? 'bg-emerald-400' : 'bg-gray-300'}`} title={agent.is_public ? 'Public' : 'Private'} />
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex items-center justify-end gap-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                            {!isSubAgent && <a href={`/?agent_id=${agent.id}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-blue-600 hover:text-blue-700 underline text-xs">Chat with agent</a>}
                                            <Button variant="ghost" size="xs" onClick={() => navigate(`${basePath}/${agent.id}/edit`)}>Edit</Button>
                                            <Button variant="ghost" size="xs" onClick={(e) => handleReload(e, agent.id)}>Reload</Button>
                                            <Button variant="ghost" size="xs" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={(e) => handleDelete(e, agent.id, agent.is_builtin)}>Delete</Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {isSubAgent && (
                <SyncPickerModal
                    open={syncPickerOpen}
                    resourceType="subagent"
                    onClose={() => { setSyncPickerOpen(false); loadAgents(); }}
                    onSuccess={(msg) => {
                        setSuccessMessage(msg);
                        loadAgents();
                        setTimeout(() => setSuccessMessage(null), 6000);
                    }}
                />
            )}
        </div>
    );
}

function SyncDropdown({ onSync, loading }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div className="relative" ref={ref}>
            <Button variant="secondary" size="sm" loading={loading} onClick={() => setOpen(!open)}>
                Sync YAML
                <svg className="w-3 h-3 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
            </Button>
            {open && (
                <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                    <button onClick={() => { onSync(false); setOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Sync new only</button>
                    <button onClick={() => { onSync(true); setOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Force sync all</button>
                </div>
            )}
        </div>
    );
}
